import { Component, Input, OnInit, OnDestroy, ViewChild, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormControl, Validators, NgForm } from '@angular/forms';
import { Item } from '../../models/item.model';
import { Country } from '../../models/country.model';
import { FilterAllPipe } from '../../pipes/filter-all.pipe';
import { knownAffiliations, availableCountries, availableActions, availableFieldsOfScience, availableDocuments, availableFormats, availableWorkPackage, adminRoles, availableAffiliations, availableFocusAreas, } from '../../constants/constants';
import { AffiliationService } from '../../services/affiliation.service';
import { TranslateService } from '../../services/translate.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { firstValueFrom, Subscription } from 'rxjs';
import { UserAffiliationService } from '../../services/user-affiliation.service';
import { UserService } from '../../services/user.service';
import { AuthService } from '../../services/auth.service';
import { DateTime } from 'luxon';
import { DateValidatorService } from '../../services/date-validator.service';
import { UserInfo } from '../../models/user.model';
import { DateService } from '../../services/date.service';
import { IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonIcon, IonContent, IonList, IonItem, IonLabel, IonInput, IonTextarea, IonSelect, IonSelectOption, IonCheckbox, IonFooter, IonListHeader, IonNote, ModalController, } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { close, add, trash, informationCircleOutline } from 'ionicons/icons';
import { ModalService } from '../../services/modal.service';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import { isApprovedExpirationOnlyExtension } from '../../utils/opportunity-update.util';

@Component({
  selector: 'app-add-modal',
  templateUrl: './add-modal.component.html',
  styleUrls: ['./add-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FilterAllPipe,
    TranslatePipe,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonIcon,
    IonContent,
    IonList,
    IonItem,
    IonLabel,
    IonInput,
    IonTextarea,
    IonSelect,
    IonSelectOption,
    IonCheckbox,
    IonFooter,
    IonListHeader,
    IonNote,
  ],
})
export class AddModalComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('offerForm') offerForm!: NgForm;
  @Input() availableActions = availableActions;
  @Input() availableCountries = availableCountries;
  availableAffiliations: any[] = [];
  isHeadcountUnlimited: boolean = false;
  @Input() availableFieldsOfScience = availableFieldsOfScience;
  @Input() isModalOpen: boolean = false;
  @Input() currentUser: UserInfo | null = null;
  @Input() page: string = '';
  @Input() itemToEdit: Item | null = null;
  isEditMode = false;
  @Input() set selectedCountry(value: Country | null) {
    if (value) {
      this._selectedCountry = value;
      this.currentItem.country = value.name;
    }
  }
  get selectedCountry(): Country | null {
    return this._selectedCountry;
  }
  private _selectedCountry: Country | null = null;
  private subscriptions: Subscription[] = [];
  private languageChangeSubscription: Subscription | null = null;
  private authSubscription: Subscription | null = null;
  private selectedFile: File | null = null;
  private customDocNames: { [key: string]: string } = {};
  private originalItem: Item | null = null;
  public tempEmailValues: string[] = [''];
  readonly availableFocusAreas = availableFocusAreas;
  displayExpirationDate: string = '';

  filteredAffiliations = availableAffiliations;
  formattedExpirationDate: string = '';
  availableDocuments = availableDocuments;
  availableFormats = availableFormats.filter(format => format.value !== 'any');
  availableWorkPackage = availableWorkPackage;
  workPackageControl = new FormControl<string[]>([], {
    nonNullable: true,
    validators: [Validators.required]
  });

  focusAreasControl = new FormControl<string[]>([], {
    nonNullable: true,
    validators: [Validators.required]
  });

  get isFormValid(): boolean {
    const formValid = this.offerForm?.form?.valid ?? false;
    const workPackageValid = this.workPackageControl?.valid && this.workPackageControl?.value?.length > 0;
    const focusAreasValid = this.focusAreasControl?.valid && this.focusAreasControl?.value?.length > 0; // ÚJ VALIDÁCIÓ
    const emailsValid = this.currentItem.contactEmails?.some(email => this.isValidEmail(email));

    return formValid && workPackageValid && focusAreasValid && emailsValid;
  }

  ngAfterViewInit() { }

  temporaryImageUrl: string | null = null;
  currentItem: Item = {
    id: '',
    title: '',
    description: '',
    opportunityType: '',
    action: '',
    country: '',
    contactEmails: [''],
    language: 'English',
    expirationDate: '',
    affiliation: '',
    fieldOfScience: '',
    focusAreas: [],
    logo: '',
    flag: 'gb',
    workPackage: [],
    headcount: 1,
    opportunityStatus: 'pending',
    documentValues: {},
    isCv: false,
    subscribers: [],
    createdAt: this.formatDateWithTimezone(new Date()),
    updatedAt: this.formatDateWithTimezone(new Date()),
    editedBy: { editorEmail: '', editorId: '', editorName: '' },
    author: { authorEmail: '', authorId: '', authorName: '' },
    userId: '',
  };
  private userRoles: string[] = [];

  constructor(
    private translateService: TranslateService,
    private userAffiliationService: UserAffiliationService,
    private userService: UserService,
    private authService: AuthService,
    private affiliationService: AffiliationService,
    public dateService: DateService,
    private dateValidatorService: DateValidatorService,
    private modalService: ModalService,
    private modalCtrl: ModalController,
  ) {
    addIcons({ close, add, trash, informationCircleOutline });
  }

  private formatDateForInput(dateString: string | undefined | null): string {
    if (!dateString) return '';
    const dt = DateTime.fromISO(dateString);
    if (!dt.isValid) return '';
    return dt.toFormat("yyyy-MM-dd'T'HH:mm");
  }

  onDisplayDateChange(event: any) {
    this.displayExpirationDate = event.detail.value;
  }

  async ngOnInit() {
    this.workPackageControl.valueChanges.subscribe(values => {
      this.currentItem.workPackage = values;
      const allowedActions = this.getFilteredActions();
      const isCurrentActionValid = allowedActions.some(action => action.value === this.currentItem.action);
      if (!isCurrentActionValid) {
        this.currentItem.action = '';
      }
    });

    this.languageChangeSubscription = this.translateService.languageChanged.subscribe(() => { });
    this.authService.userRoles$.subscribe(roles => {
      this.userRoles = roles;
    });

    const affiliationsSub = this.affiliationService.affiliations$.subscribe(affiliations => {
      this.availableAffiliations = affiliations;
    });
    this.subscriptions.push(affiliationsSub);

    this.focusAreasControl.valueChanges.subscribe(values => {
      this.currentItem.focusAreas = values;
    });

    this.filteredAffiliations = await this.userAffiliationService.getFilteredAffiliations();

    if (this.itemToEdit) {
      this.isEditMode = true;
      this.originalItem = JSON.parse(JSON.stringify(this.itemToEdit));
      this.currentItem = JSON.parse(JSON.stringify(this.originalItem));
      if (!this.isAffiliationInList(this.currentItem.affiliation, this.filteredAffiliations)) {
        this.addUserAffiliationToList(this.currentItem.affiliation);
      }
      this.displayExpirationDate = this.formatDateForInput(this.currentItem.expirationDate);
      if (this.currentItem.expirationDate) {
        this.currentItem.expirationDate = this.formatDateForInput(this.currentItem.expirationDate);
      }
      this.workPackageControl.setValue(this.currentItem.workPackage || []);
      this.focusAreasControl.setValue(this.currentItem.focusAreas || []);
      this.isHeadcountUnlimited = this.currentItem.headcount === 0;
      this.tempEmailValues = [...(this.currentItem.contactEmails || [''])];
      const country = this.availableCountries.find(c => c.name === this.currentItem.country);
      this._selectedCountry = country || null;
    }

    this.authSubscription = this.authService.isLoggedIn$.subscribe(async (isLoggedIn) => {
      if (isLoggedIn) {
        try {
          const userInfo = await this.userService.getCurrentUserInfo();
          const userAffiliation = userInfo.affiliation;
          if (userAffiliation && !this.isAffiliationInList(userAffiliation, this.filteredAffiliations)) {
            this.addUserAffiliationToList(userAffiliation);
          }
          if (!this.isEditMode) {
            if (!this.isAdmin()) {
              this.currentItem.affiliation = userInfo.affiliation || '';
            }
            this.currentItem.author = { authorEmail: userInfo.email, authorId: userInfo.id, authorName: userInfo.name };
            this.currentItem.contactEmails = [userInfo.email];
            this.tempEmailValues = [userInfo.email];
            this.currentItem.userId = userInfo.id;
          }
          this.currentItem.editedBy = { editorEmail: userInfo.email, editorId: userInfo.id, editorName: userInfo.name };
        } catch (error) {
          console.error('Error getting user info:', error);
          this.closeModal();
        }
      } else {
        this.closeModal();
      }
    });
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    if (this.languageChangeSubscription) this.languageChangeSubscription.unsubscribe();
    if (this.authSubscription) this.authSubscription.unsubscribe();
  }

  getFilteredActions(): any[] {
    const openTo = this.workPackageControl.value;
    if (!openTo || openTo.length === 0) {
      return this.availableActions;
    }

    const allowedActions = new Set<string>();
    openTo.forEach((type: string) => {
      switch (type) {
        case 'student':
          allowedActions.add('internship');
          allowedActions.add('phd');
          allowedActions.add('innovate');
          allowedActions.add('other');
          break;
        case 'academic-staff':
          allowedActions.add('joint-course');
          allowedActions.add('lecture');
          allowedActions.add('conference');
          allowedActions.add('eu-project-call');
          allowedActions.add('research-stay');
          allowedActions.add('innovate');
          allowedActions.add('job-shadowing');
          allowedActions.add('internship');
          allowedActions.add('phd');
          allowedActions.add('other');
          break;
        case 'administrative-staff':
          allowedActions.add('joint-course');
          allowedActions.add('eu-project-call');
          allowedActions.add('innovate');
          allowedActions.add('job-shadowing');
          allowedActions.add('training-opportunity');
          allowedActions.add('other');
          break;
        case 'non-university-stakeholder':
          allowedActions.add('joint-course');
          allowedActions.add('eu-project-call');
          allowedActions.add('research-stay');
          allowedActions.add('innovate');
          allowedActions.add('internship');
          allowedActions.add('other');
          break;
      }
    });

    return this.availableActions.filter(action => allowedActions.has(action.value));
  }

  async closeModal() {
    if (this.offerForm?.dirty) {
      const modal = await this.modalCtrl.create({
        component: ConfirmationModalComponent,
        componentProps: {
          title: 'UNSAVED_CHANGES_TITLE',
          message: 'UNSAVED_CHANGES_MESSAGE',
          confirmButtonText: 'DISCARD',
          cancelButtonText: 'CONTINUE_EDITING'
        }
      });
      await modal.present();
      const { data } = await modal.onDidDismiss();
      if (data?.confirmed) {
        this.modalCtrl.dismiss();
      }
    } else {
      this.modalCtrl.dismiss();
    }
  }

  async showCountryInfo(event: Event) {
    event.stopPropagation();
    await this.modalService.openConfirmationModal({
      title: 'COUNTRY',
      message: 'ONLINE_OPPORTUNITY_INFO',
      isError: true,
      confirmText: 'CLOSE'
    });
  }

  async confirmDelete() {
    const modal = await this.modalCtrl.create({
      component: ConfirmationModalComponent,
      componentProps: {
        title: 'DELETE_CONFIRMATION',
        message: 'DELETE_CONFIRMATION_MESSAGE',
        confirmButtonText: 'DELETE',
        cancelButtonText: 'CANCEL'
      }
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();
    if (data?.confirmed) {
      this.modalCtrl.dismiss({ deleted: true, item: this.currentItem });
    }
  }

  async openSaveConfirmModal() {
    if (!this.isFormValid) return;
    const modal = await this.modalCtrl.create({
      component: ConfirmationModalComponent,
      componentProps: {
        title: 'ARE_YOU_SURE_YOU_WANT_TO_SAVE',
        message: 'ARE_YOU_SURE_YOU_WANT_TO_SAVE_CHANGES',
        confirmButtonText: 'SAVE',
        cancelButtonText: 'CANCEL',
      }
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();
    if (data?.confirmed) {
      this.saveOffer();
    }
  }

  private async saveOffer() {
    try {
      if (this.isHeadcountUnlimited) {
        this.currentItem.headcount = 0;
      }

      if (!this.authService.getCurrentUser()) {
        throw new Error('User must be logged in to save offer');
      }

      this.currentItem.workPackage = this.workPackageControl.value;
      this.currentItem.focusAreas = this.focusAreasControl.value;

      if (this.displayExpirationDate) {
        const dt = DateTime.fromISO(this.displayExpirationDate);

        if (!dt.isValid) {
          console.error('Invalid expiration date format in UI');
          return;
        }

        const now = DateTime.now();
        this.currentItem.expirationDate = (dt < now ? now.toISO() : dt.toISO()) as string;
      }

      const nowJS = new Date();
      if (!this.isEditMode) {
        this.currentItem.createdAt = this.formatDateWithTimezone(nowJS);
      }
      this.currentItem.updatedAt = this.formatDateWithTimezone(nowJS);

      const allAffiliations = this.affiliationService.getAffiliations();
      const selectedAffiliation = allAffiliations.find(a => a.value === this.currentItem.affiliation);
      if (selectedAffiliation && selectedAffiliation.logo) {
        this.currentItem.logo = selectedAffiliation.logo;
      } else {
        const euglohAffiliation = allAffiliations.find(a => a.value === 'eugloh-secretariat');
        if (euglohAffiliation) {
          this.currentItem.logo = euglohAffiliation.logo;
        }
      }

      if (!this.isEditMode) {
        this.currentItem.opportunityStatus = this.isAdmin() ? 'active' : 'pending';
      } else if (this.isEditMode && !this.isAdmin()) {
        this.currentItem.opportunityStatus = this.originalItem &&
          isApprovedExpirationOnlyExtension(this.originalItem, this.currentItem)
          ? 'active'
          : 'pending';
      }

      const offerToSave = { ...this.currentItem };
      this.modalService.dismiss({ savedOffer: offerToSave });

    } catch (error) {
      console.error('Error saving offer:', error);
      this.modalCtrl.dismiss({ savedOffer: { ...this.currentItem } });
    }
  }

  async openCountryModal() {
    const selected = await this.modalService.openCountrySelectionModal(this.selectedCountry);
    if (selected) {
      this.selectedCountry = selected;
    }
  }

  private formatDateWithTimezone(date: Date): string {
    return DateTime.fromJSDate(date).toISO() || '';
  }

  isAdmin(): boolean {
    return this.userRoles.some(role => adminRoles.includes(role));
  }

  getAffiliationName(value: string): string {
    const affiliation = this.filteredAffiliations.find(a => a.value === value);
    return affiliation?.name || '';
  }

  private isAffiliationInList(affiliationValue: string, affiliationList: any[]): boolean {
    return affiliationList.some(aff => aff.value === affiliationValue);
  }

  private addUserAffiliationToList(affiliationValue: string): void {
    if (affiliationValue && affiliationValue.trim() !== '') {
      const userAffiliation = knownAffiliations.find(affiliation => affiliation.value === affiliationValue) || {
        value: affiliationValue,
        name: affiliationValue,
        isFilter: true,
        logo: '',
      };
      this.filteredAffiliations = [
        ...this.filteredAffiliations.filter(affiliation => affiliation.value !== 'other'),
        userAffiliation,
        ...this.filteredAffiliations.filter(affiliation => affiliation.value === 'other')
      ];
    }
  }

  isValidEmail(email: string): boolean {
    if (!email) return false;
    const re = /\S+@\S+\.\S+/;
    return re.test(email);
  }

  addNewEmail() {
    this.tempEmailValues.push('');
    this.currentItem.contactEmails.push('');
  }

  removeEmail(index: number) {
    if (this.currentItem.contactEmails.length > 1) {
      this.tempEmailValues.splice(index, 1);
      this.currentItem.contactEmails.splice(index, 1);
    }
  }

  onEmailInput(index: number, value: any) {
    this.tempEmailValues[index] = value.detail.value!;
    this.currentItem.contactEmails[index] = value.detail.value!;
  }

  addNewDocument() {
    const usedTypes = Object.keys(this.currentItem.documentValues);
    const availableType = this.availableDocuments.find(doc => !usedTypes.includes(doc.value));
    if (availableType) {
      this.currentItem.documentValues[availableType.value] = ['any'];
    }
  }

  removeDocument(documentType: string) {
    delete this.currentItem.documentValues[documentType];
  }

  updateDocumentType(oldType: string, event: any) {
    const newType = event.detail.value;
    if (oldType === newType || !this.currentItem.documentValues[oldType]) return;
    const formats = this.currentItem.documentValues[oldType];
    delete this.currentItem.documentValues[oldType];
    this.currentItem.documentValues[newType] = formats;
  }

  getCustomDocumentName(docType: string): string {
    return this.customDocNames[docType] || '';
  }

  updateCustomDocumentName(docType: string, event: any) {
    const name = event.detail.value!;
    this.customDocNames[docType] = name;
  }

  toggleFormat(documentType: string, format: string, event: any) {
    const isChecked = event.detail.checked;
    let currentFormats = this.currentItem.documentValues[documentType] || [];
    if (isChecked && format !== 'any') {
      currentFormats = currentFormats.filter(f => f !== 'any');
    }
    const index = currentFormats.indexOf(format);
    if (isChecked && index === -1) {
      currentFormats.push(format);
    } else if (!isChecked && index !== -1) {
      currentFormats.splice(index, 1);
    }
    if (currentFormats.length === 0) {
      currentFormats.push('any');
    }
    this.currentItem.documentValues[documentType] = currentFormats;
  }

  isFormatSelected(documentType: string, format: string): boolean {
    return this.currentItem.documentValues[documentType]?.includes(format) || false;
  }

  getDocumentTypes(): string[] {
    return Object.keys(this.currentItem.documentValues);
  }

  onHeadcountChange(event: any) {
    const value = event.detail.value!;
    if (!this.isHeadcountUnlimited) {
      let numValue = parseInt(value, 10);
      if (isNaN(numValue) || numValue < 1) {
        numValue = 1;
      }
      this.currentItem.headcount = numValue;
      if (event.target.value !== numValue.toString()) {
        event.target.value = numValue;
      }
    }
  }

  onExpirationDateChange(event: Event): void {
    this.currentItem.expirationDate = this.dateValidatorService.handleExpirationDateChange(event, this.currentItem.expirationDate);
  }

  getCurrentDateTime(): string {
    return this.dateService.getCurrentDateTime();
  }
}
